import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import Button from "./Button";
import Player from "./Types/Player";

const popupAnimation = keyframes`
  0%{
    transform: scale(0.4);
  }
  50%{
    transform: scale(1.1);
  }
  100%{
    transform: scale(1);
  }
`;

const StyledInput = styled.input`
  padding: 10px;
  margin: 10px;
`;

const PopUpContainer = styled.div`
  z-index: 100;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  background-color: rgba(0, 0, 0, 0.5);
`;

const InnerPopup = styled.div`
  animation: ${popupAnimation} 0.3s linear forwards;
  text-align: center;
  color: black;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 25%;
  right: 25%;
  top: 25%;
  bottom: 25%;
  margin: auto;
  border-radius: 20px;
  background: beige;
  font-size: 1.5rem;
  z-index: 200;

  input {
    font-size: 1rem;
    border-radius: 0.4em;
    font-family: inherit;
    font-weight: bold;
    padding: 0.8em 1em;
    border: 0.2em solid #80805c;
  }
`;

const ErrorMessage = styled.div`
  padding: 10px;
  position: absolute;
`;

interface PopupProps {
  onOverlayClick?: () => void;
}

export const withPopup =
  <P extends object>(
    WrappedComponent: React.ComponentType<P>
  ): React.FC<P & PopupProps> =>
  ({ onOverlayClick, ...props }) =>
    (
      <PopUpContainer onClick={onOverlayClick}>
        <InnerPopup onClick={(e) => e.stopPropagation()}>
          <WrappedComponent {...(props as P)} />
        </InnerPopup>
      </PopUpContainer>
    );

interface NamePromptProps {
  onSubmit: (name: string) => void;
  lastUsedName: string;
  names: string[];
  nameErrorMessage?: string;
}

const NamePromptContent: React.SFC<NamePromptProps> = ({
  onSubmit,
  lastUsedName,
  names,
  nameErrorMessage,
}: NamePromptProps) => {
  const [name, setName] = useState(lastUsedName);
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");

  React.useEffect(() => {
    setErrorMessage(nameErrorMessage);
  }, [nameErrorMessage]);

  const handleOnSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log(name);
    if (name === undefined || name.trim() === "") {
      setErrorMessage("You must have a name...");
      return;
    }
    if (names.some((n) => n === name)) {
      setErrorMessage("This name is already taken 😬");
    } else {
      onSubmit(name);
    }
  };
  return (
    <div>
      <h1>What is your name?</h1>
      <form onSubmit={handleOnSubmit}>
        <StyledInput
          value={name}
          onChange={(v) => setName(v.target.value)}
          maxLength={25}
        />
        <Button type="submit">Submit</Button>
      </form>
      <ErrorMessage>{errorMessage}</ErrorMessage>
    </div>
  );
};

interface ConfirmPopupProps {
  onSubmit: () => void;
  onCancel: () => void;
}

const ConfirmPopupContent = ({ onSubmit, onCancel }: ConfirmPopupProps) => {
  const handleOnSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <>
      <h1>Do you really want to end the game session?</h1>
      <form onSubmit={handleOnSubmit}>
        <Button isDark={true} animated={true} onClick={onCancel}>
          NO
        </Button>
        <Button type="submit" isDark={false} animated={false}>
          YES
        </Button>
      </form>
    </>
  );
};
interface RulesPopupContentProps {
  onClose: () => void;
  children: React.ReactNode;
}

const Container = styled.div`
  margin: 1em;
  overflow: auto;
  scrollbar-width: thin;
`;

const RulesPopupContent = ({ onClose, children }: RulesPopupContentProps) => {
  return (
    <Container>
      {children}
      <Button isDark={false} animated={false} onClick={onClose}>
        Close
      </Button>
    </Container>
  );
};

const DisconnectedPlayersPopupContent = ({
  players,
  onRemovePlayer,
}: {
  players: Player[];
  onRemovePlayer: (name: string) => void;
}) => {
  return (
    <>
      <h1>
        Ops, looks like the following players disconnected from the session.
      </h1>
      <p>
        You can either wait for them to reconnect or remove them from the
        session.
      </p>

      <ul>
        {players
          .filter((p) => !p.isConnected)
          .map((p) => (
            <li key={`${p.name}`}>
              {p.name}
              <Button onClick={() => onRemovePlayer(p.name)} isSmall={true}>
                Remove from game
              </Button>
            </li>
          ))}
      </ul>
    </>
  );
};

export const NamePrompt = withPopup(NamePromptContent);
export const ConfirmPopup = withPopup(ConfirmPopupContent);
export const RulesPopup = withPopup(RulesPopupContent);
export const DisconnectedPlayersPopup = withPopup(
  DisconnectedPlayersPopupContent
);
